import React, { useEffect, useState } from 'react';
import AuthLayout from '@/components/auth/AuthLayout';
import LoginForm from '@/components/auth/LoginForm';
import PasswordReset from '@/components/auth/PasswordReset';
import UpdatePasswordForm from '@/components/auth/UpdatePasswordForm';
import SEOHead from '@/components/seo/SEOHead';
import { useAuthPage } from '@/hooks/useAuthPage';
import useSession from "@/v2/hooks/supabase/useSession";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";

const Auth = () => {
  const {
    loading,
    setLoading,
    view,
    handleSetView,
    handlePasswordUpdate,
    setView,
    setRecoveryToken,
  } = useAuthPage();

  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const [emailConfirmationStatus, setEmailConfirmationStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [emailConfirmationError, setEmailConfirmationError] = useState<string | null>(null);
  const [isPasswordResetSession, setIsPasswordResetSession] = useState(false);

  // Check if we're in the password reset flow
  const isPasswordResetFlow = () => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const typeParam = searchParams.get('type');
    return viewParam === 'update-password' || typeParam === 'recovery';
  };

  // Check if the current session is from a password reset
  useEffect(() => {
    const checkPasswordResetSession = async () => {
      if (session && isPasswordResetFlow()) {
        // Check if this is a password reset session by looking at the user's metadata
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error('Error getting user:', error);
          return;
        }

        // If the user has a recovery token in their metadata, this is a password reset session
        const isRecoverySession = user?.app_metadata?.provider === 'email' && 
                                user?.app_metadata?.provider_id === 'email' &&
                                location.hash.includes('access_token');

        setIsPasswordResetSession(isRecoverySession);

        // If this is a password reset session, we should clear any existing session
        if (isRecoverySession) {
          // Don't sign out, just clear the session storage
          localStorage.removeItem('SUPABASE_SESSION');
        }
      }
    };

    checkPasswordResetSession();
  }, [session, location.hash]);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Parse both search params and hash fragment
      const searchParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.substring(1)); // Remove the # from the hash
      
      const type = searchParams.get('type');
      const token = searchParams.get('token');
      const message = hashParams.get('message');
      const next = searchParams.get('next') || '/dashboard/settings';

      // Handle hash-based confirmation (new Supabase flow)
      if (message === 'Confirmation link accepted. Please proceed to confirm link sent to the other email') {
        try {
          setLoading(true);
          setEmailConfirmationStatus('processing');
          setEmailConfirmationError(null);

          // Get the current session to verify the email change
          const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            throw sessionError;
          }

          if (!currentSession) {
            throw new Error('No active session found');
          }

          // The email change is already confirmed by Supabase at this point
          // We just need to handle the UI and redirect
          setEmailConfirmationStatus('success');
          toast({
            title: "Email updated successfully",
            description: "Your email has been updated. You can now use your new email to sign in.",
          });

          // Add a small delay to show the success message before redirecting
          setTimeout(() => {
            navigate(next);
          }, 2000);
        } catch (error: any) {
          console.error('Email confirmation error:', error);
          setEmailConfirmationStatus('error');
          setEmailConfirmationError(error.message || "Failed to confirm email change. Please try again.");
          toast({
            title: "Error confirming email change",
            description: error.message || "Failed to confirm email change. Please try again.",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
        return;
      }

      // Handle query param-based confirmation (old flow)
      if (type === 'email_change' && token) {
        try {
          setLoading(true);
          setEmailConfirmationStatus('processing');
          setEmailConfirmationError(null);

          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'email_change'
          });

          if (error) {
            throw error;
          }

          setEmailConfirmationStatus('success');
          toast({
            title: "Email updated successfully",
            description: "Your email has been updated. You can now use your new email to sign in.",
          });

          // Add a small delay to show the success message before redirecting
          setTimeout(() => {
            navigate(next);
          }, 2000);
        } catch (error: any) {
          console.error('Email confirmation error:', error);
          setEmailConfirmationStatus('error');
          setEmailConfirmationError(error.message || "Failed to confirm email change. Please try again.");
          toast({
            title: "Error confirming email change",
            description: error.message || "Failed to confirm email change. Please try again.",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    handleEmailConfirmation();
  }, [location.search, location.hash, setLoading, toast, navigate]);

  // Process URL parameters for view changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const viewParam = searchParams.get('view');
    const typeParam = searchParams.get('type');

    console.log("URL parameters:", { tabParam, viewParam, typeParam });

    if (viewParam === 'reset-password') {
      setView('password-reset');
    } else if (viewParam === 'update-password' || typeParam === 'recovery') {
      setView('update-password');
      console.log("Setting view to update-password");
      
      // Store the recovery token if present
      const hash = location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken) {
          console.log("Recovery token found in URL");
          setRecoveryToken(accessToken);
          sessionStorage.setItem("recoveryToken", accessToken);
          if (refreshToken) {
            sessionStorage.setItem("recoveryRefreshToken", refreshToken);
          }
          
          // Clear the hash from the URL to prevent issues with subsequent navigation
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    } else if (tabParam === 'signup') {
      setView('signup');
    } else if (tabParam === 'login') {
      setView('login');
    } else {
      setView('login');
    }
  }, [location.search, location.hash, setView, setRecoveryToken]);

  const seoHead = (
    <SEOHead
      title="Log in to your WCD account"
      description="For existing clients and designers who already have an account. Founders hiring for the first time: book a free intro at world-class.design instead."
    />
  );

  // Show email confirmation status if processing
  if (emailConfirmationStatus === 'processing') {
    return (
      <>
        {seoHead}
        <AuthLayout>
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-lg font-medium">Confirming your email change...</p>
          </div>
        </AuthLayout>
      </>
    );
  }

  // Show email confirmation error if failed
  if (emailConfirmationStatus === 'error') {
    return (
      <>
        {seoHead}
        <AuthLayout>
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {emailConfirmationError}
            </AlertDescription>
          </Alert>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/dashboard/settings')}
              className="text-sm text-primary hover:underline"
            >
              Return to Settings
            </button>
          </div>
        </div>
      </AuthLayout>
      </>
    );
  }

  // Show success message if confirmed
  if (emailConfirmationStatus === 'success') {
    return (
      <>
        {seoHead}
        <AuthLayout>
        <div className="flex flex-col items-center justify-center space-y-4 py-8">
          <div className="rounded-full bg-green-100 p-3">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p className="text-lg font-medium text-green-600">Email updated successfully!</p>
          <p className="text-sm text-muted-foreground">Redirecting you back to settings...</p>
        </div>
      </AuthLayout>
      </>
    );
  }

  const renderAuthContent = () => {
    switch (view) {
      case 'password-reset':
        return <PasswordReset setView={handleSetView} />;
      case 'update-password':
        return (
          <UpdatePasswordForm
            loading={loading}
            setLoading={setLoading}
            onPasswordUpdate={handlePasswordUpdate}
          />
        );
      default:
        return (
          <LoginForm
            loading={loading}
            setLoading={setLoading}
            onForgotPassword={() => handleSetView('password-reset')}
            returnTo={returnTo}
          />
        );
    }
  };

  return (
    <>
      {seoHead}
      <AuthLayout>
        {renderAuthContent()}
        {/* Log in is for existing accounts only. New founders don't need an
            account to hire — their first step is the free intro call. */}
        <p className="mt-6 max-w-sm mx-auto text-center text-sm text-gray-600">
          Founder hiring for the first time? You don&apos;t need an account.{" "}
          <a
            href="https://cal.com/guidione-machava-1gryqg/client-intro-call"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#2B1B1C] underline underline-offset-4 hover:opacity-80"
          >
            Book a free intro
          </a>{" "}
          at world-class.design instead.
        </p>
      </AuthLayout>
    </>
  );
};

export default Auth;
